'use client'

/**
 * The source tier at the end of a Home claim (#16825, PRD R2.1): "Your
 * district", "General" or "Best practice".
 *
 * The tier is derived server-side from what the answer cited
 * (`@/lib/ai-chat/source-tiers`); this only names it. Not
 * `ProvenancePill`: that pill is a button opening a popover of a source's
 * detail, and a tier has no detail to open — a badge that does nothing when
 * pressed must not look or behave like a control. So it is the design
 * system's `Badge`, non-interactive, with its label as plain text.
 *
 * Semantic tokens only, through `Badge`'s variants: the district tier takes
 * the filled `secondary` treatment, because it is the one a leader can
 * trust as their own; the other two are outlined.
 */

import { Badge } from '@/components/ui/badge'
import { SOURCE_TIER_LABELS, type SourceTier } from '@/lib/ai-chat/source-tiers'

const TIER_VARIANTS = {
  district: 'secondary',
  general: 'outline',
  best_practice: 'outline',
} as const satisfies Record<SourceTier, 'secondary' | 'outline'>

export function TierBadge({ tier }: { tier: SourceTier }) {
  return (
    <Badge
      data-testid="ai-chat-tier-badge"
      data-tier={tier}
      variant={TIER_VARIANTS[tier]}
      className="shrink-0 whitespace-nowrap"
    >
      {SOURCE_TIER_LABELS[tier]}
    </Badge>
  )
}
